import { useMemo, useState } from 'react';
import { useTranslation } from '@/i18n';
import { rankAirlines, useAirlines } from '@/lib/api/airlines';
import { CodeFilter } from './CodeFilter';
import type { CodeOption } from './CodeFilter';

/**
 * The airline column, over the airline dictionary.
 *
 * `/airline/all` comes back as one response and is kept for the page's lifetime, so
 * the matching happens in the browser and no request is made per keystroke. The
 * interaction — open on a click, search as you type, commit on a pick — lives in
 * CodeFilter, which the airport columns share.
 */

/**
 * How many options are built at once.
 *
 * A click on an empty field offers the whole dictionary — the list scrolls, and
 * scanning it is the point. This cap is therefore not about readability but about
 * DOM: `/airline/all` is every airline that exists, and a thousand buttons cost more
 * to build than anyone will ever scroll. What is cut off is stated, so a short list
 * is never mistaken for the whole one.
 */
const MAX_OPTIONS = 100;

interface AirlineFilterProps {
  value: string;
  onChange: (value: string) => void;
  placeholder: string;
  ariaLabel: string;
}

export const AirlineFilter = ({ value, onChange, placeholder, ariaLabel }: AirlineFilterProps) => {
  const { t } = useTranslation();
  const { airlines, isLoading, hasFailed } = useAirlines();
  const [query, setQuery] = useState(value);

  // Ranked in lib/api/airlines.ts — the user screen picks carriers the same way.
  const found = useMemo<CodeOption[]>(
    () =>
      rankAirlines(airlines, query).map((airline) => ({
        code: (airline.iataCode ?? '').toUpperCase(),
        label: airline.title ?? '',
        key: airline.id,
      })),
    [airlines, query],
  );
  const options = found.slice(0, MAX_OPTIONS);
  const hidden = found.length - options.length;

  return (
    // One code: the endpoint has no list form for the airline, only `LIKE`.
    <CodeFilter
      values={value === '' ? [] : [value]}
      onChange={(codes) => onChange(codes[0] ?? '')}
      placeholder={placeholder}
      ariaLabel={ariaLabel}
      options={options}
      onQueryChange={setQuery}
      isLoading={isLoading}
      emptyLabel={hasFailed ? t('schedule.airlinesFailed') : t('schedule.noMatches')}
      footnote={hidden > 0 ? t('schedule.moreMatches', { count: hidden }) : null}
    />
  );
};
